import React, { Component } from 'react';

import {
	MAX_FILE_SIZE_MB,
	MAX_FILE_SIZE_BYTES,
	fetchLibrary,
	uploadResource,
	resourceErrorText
} from '../../services/user-resources';

// Selector de imagenes de la biblioteca personal para los editores de texto.
//
// Es un boton que despliega un panel con los recursos del usuario; al pulsar uno
// se inserta en el editor por el punto donde esta el cursor. Desde el mismo
// panel se pueden subir imagenes nuevas, para no tener que salir a /resources
// solo para dejar un archivo y volver.
//
// Vive como componente porque lo usan cuatro editores (los dos de project-edit y
// los dos de create-project). La primera version estaba escrita dentro de
// project-edit, y duplicarla era la via rapida a arreglar un fallo en un sitio y
// no en el otro, igual que paso con el adaptador de subida.
//
// La pagina solo aporta getEditor: quien posee la instancia de CKEditor es ella.
export default class ResourcePicker extends Component {
	state = {
		open: false,
		loading: false,
		error: "",
		notice: "",
		folders: [],
		resources: [],
		quota: {used: 0, max: 0, max_file_size_mb: MAX_FILE_SIZE_MB},
		upload_folder: "",
		uploading: false,
		progress: null
	}

	fileInputRef = React.createRef()

	toggle = async () => {
		if (this.state.open) {
			this.setState({open: false});
			return;
		}

		this.setState({open: true});
		await this.load();
	}

	// Se relee en cada apertura: la biblioteca esta capada por cuota, asi que es
	// una lista corta, y asi aparecen las imagenes que se hayan subido desde otra
	// pestaña o desde la propia pagina Resources.
	load = async () => {
		this.setState({loading: true, error: ""});

		try {
			const data = await fetchLibrary();

			this.setState({
				folders: data.folders || [],
				resources: data.resources || [],
				quota: data.quota || this.state.quota,
				loading: false
			});
		} catch (error) {
			console.log(error);
			this.setState({loading: false, error: resourceErrorText(error)});
		}
	}

	// ----------------------------------------------------
	// SUBIDA ---------------------------------------------
	// ----------------------------------------------------

	pickFiles = () => {
		if (this.fileInputRef.current) {
			this.fileInputRef.current.click();
		}
	}

	onFilesPicked = async (e) => {
		const picked = Array.from(e.target.files || []);

		// Se limpia ya: sin esto, elegir el mismo archivo otra vez no dispara
		// change y parece que el boton ha dejado de funcionar.
		e.target.value = "";

		if (picked.length === 0) {
			return;
		}

		await this.uploadFiles(picked);
	}

	uploadFiles = async (files) => {
		const {quota, upload_folder} = this.state;
		const room = quota.max - quota.used;

		if (room <= 0) {
			this.setState({error: `You have reached the limit of ${quota.max} resources. Delete one in Resources to upload another, or contact Seat 7 if you require more files.`});
			return;
		}

		// Se recorta antes de empezar en vez de dejar que el servidor rechace las
		// ultimas: asi el aviso dice cuantas entran y cuantas se quedan fuera.
		const accepted = files.slice(0, room);
		const rejectedByQuota = files.length - accepted.length;
		const tooBig = accepted.filter(file => file.size > MAX_FILE_SIZE_BYTES);
		const notImages = accepted.filter(file => !file.type.startsWith('image/'));
		const valid = accepted.filter(file =>
			file.size <= MAX_FILE_SIZE_BYTES && file.type.startsWith('image/')
		);

		this.setState({uploading: true, error: "", notice: ""});

		const failed = [];
		let uploaded = 0;

		for (let i = 0; i < valid.length; i++) {
			this.setState({progress: {current: i + 1, total: valid.length}});

			try {
				await uploadResource(valid[i], upload_folder || null);
				uploaded++;
			} catch (error) {
				console.log(error);
				failed.push(`${valid[i].name}: ${resourceErrorText(error)}`);
			}
		}

		this.setState({uploading: false, progress: null});
		await this.load();

		const problems = [];

		if (rejectedByQuota > 0) {
			problems.push(`${rejectedByQuota} left out: only ${room} slot${room === 1 ? '' : 's'} left`);
		}

		if (tooBig.length > 0) {
			problems.push(`${tooBig.length} over ${MAX_FILE_SIZE_MB} MB`);
		}

		if (notImages.length > 0) {
			problems.push(`${notImages.length} not an image`);
		}

		problems.push(...failed);

		// No se inserta sola la recien subida: con varias no habria forma de
		// elegir cual, asi que aparecen en la rejilla y se pulsa la que toque.
		this.setState({
			notice: uploaded > 0
				? `${uploaded} image${uploaded === 1 ? '' : 's'} added to your resources. Click one to insert it.`
				: "",
			error: problems.length > 0 ? problems.join('. ') : ""
		});
	}

	// ----------------------------------------------------
	// INSERCION ------------------------------------------
	// ----------------------------------------------------

	insert = (resource) => {
		const editor = this.props.getEditor && this.props.getEditor();

		if (!editor) {
			this.setState({error: "The editor is not ready yet."});
			return;
		}

		// El build classic v20 que usa la app trae Image e ImageUpload pero no
		// ImageInsert, asi que no hay comando que ejecutar y se inserta por la API
		// del modelo. Su elemento se llama 'image' y admite src y alt; de v35 en
		// adelante pasa a imageBlock y aparece insertImage, de ahi que se pregunte
		// antes por el comando en vez de dar por hecha una de las dos formas.
		const commandName = ['insertImage', 'imageInsert']
			.find(name => editor.commands.get(name));

		if (commandName) {
			editor.execute(commandName, {source: resource.url});
		} else {
			editor.model.change(writer => {
				const image = writer.createElement('image', {
					src: resource.url,
					// El nombre del recurso como alt: es lo unico que describe la
					// imagen, y este texto acaba publicado en la landing page.
					alt: resource.name,
				});

				editor.model.insertContent(image, editor.model.document.selection);
			});
		}

		// El foco vuelve al texto: sin esto el cursor se queda en el boton del
		// panel y hay que volver a pinchar en el editor para seguir escribiendo.
		editor.editing.view.focus();
		this.setState({open: false, notice: "", error: ""});
	}

	// ----------------------------------------------------
	// RENDER ---------------------------------------------
	// ----------------------------------------------------

	renderPanel() {
		const {
			loading, error, notice, folders, resources, quota,
			upload_folder, uploading, progress
		} = this.state;

		const full = quota.used >= quota.max && quota.max > 0;

		return (
			<div className="resource-picker-panel">
				<div className="picker-toolbar">
					<span className={`picker-quota ${full ? 'is-full' : ''}`}>
						{quota.used} / {quota.max} images
					</span>

					{folders.length > 0 && (
						<label className="picker-folder">
							<span className="visually-hidden">Folder for new uploads</span>
							<select
								value={upload_folder}
								onChange={e => this.setState({upload_folder: e.target.value})}
							>
								<option value="">All resources (root)</option>
								{folders.map(folder => (
									<option value={folder.id} key={`picker-folder-${folder.id}`}>
										{folder.name}
									</option>
								))}
							</select>
						</label>
					)}

					<input
						ref={this.fileInputRef}
						type="file"
						className="visually-hidden"
						accept="image/*"
						multiple
						onChange={this.onFilesPicked}
					/>

					<button
						type="button"
						className="btn blue picker-upload"
						disabled={uploading || full}
						onClick={this.pickFiles}
					>
						{uploading ? 'Uploading…' : 'Upload new'}
					</button>
				</div>

				{progress && (
					<p className="picker-message">Uploading {progress.current} of {progress.total}…</p>
				)}

				{notice && <p className="picker-message is-notice">{notice}</p>}

				{error && <p className="picker-message is-error">{error}</p>}

				{loading && <p className="picker-message">Loading your resources…</p>}

				{!loading && resources.length === 0 && (
					<p className="picker-message">
						You have no resources yet. Use “Upload new” to add your first image.
					</p>
				)}

				{!loading && resources.length > 0 && (
					<div className="picker-grid">
						{resources.map(resource => (
							<button
								type="button"
								className="picker-item"
								key={`picker-${resource.id}`}
								title={resource.name}
								onClick={() => this.insert(resource)}
							>
								<img src={resource.url} alt={resource.name} loading="lazy" />
								<span className="picker-name">{resource.name}</span>
							</button>
						))}
					</div>
				)}
			</div>
		);
	}

	render() {
		return (
			<div className="resource-picker-bar">
				<button
					type="button"
					className="btn grey btn-resources"
					aria-expanded={this.state.open}
					onClick={this.toggle}
				>{this.props.label || 'Insert from Resources'}</button>

				{this.state.open && this.renderPanel()}
			</div>
		);
	}
}
